"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { Modal, ModalContent, ModalBody, Kbd, Button } from "@nextui-org/react";
import { Search } from "lucide-react";
import { flattenTools } from "@/lib/urls";
import { ToolIcon } from "@/lib/toolIcons";

export default function QuickSwitch() {
    const [isOpen, setIsOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [activeIndex, setActiveIndex] = useState(0);
    const inputRef = useRef<HTMLInputElement>(null);
    const router = useRouter();
    const tools = useMemo(() => flattenTools(), []);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return tools;
        return tools.filter((t) => t.label.toLowerCase().includes(q) || t.group.toLowerCase().includes(q));
    }, [tools, query]);

    const close = () => {
        setIsOpen(false);
        setQuery("");
        setActiveIndex(0);
    };

    const navigateTo = (path: string) => {
        router.push(path);
        close();
    };

    useEffect(() => {
        const handleGlobalKeyDown = (e: KeyboardEvent) => {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
                e.preventDefault();
                setIsOpen(true);
            }
        };
        window.addEventListener("keydown", handleGlobalKeyDown);
        return () => window.removeEventListener("keydown", handleGlobalKeyDown);
    }, []);

    useEffect(() => {
        setActiveIndex(0);
    }, [query]);

    useEffect(() => {
        if (isOpen) {
            setTimeout(() => inputRef.current?.focus(), 50);
        }
    }, [isOpen]);

    const handleInputKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActiveIndex((i) => Math.max(i - 1, 0));
        } else if (e.key === "Enter") {
            e.preventDefault();
            if (filtered[activeIndex]) navigateTo(filtered[activeIndex].path);
        }
    };

    return (
        <>
            <Button
                variant="flat"
                radius="full"
                className="min-w-0 px-3 gap-2 text-gray-500 dark:text-gray-400"
                startContent={<Search className="w-4 h-4" aria-hidden="true" />}
                onClick={() => setIsOpen(true)}
                aria-label="Search tools"
            >
                <span className="hidden lg:inline text-sm">Quick switch</span>
                <Kbd keys={["command"]} className="hidden lg:inline-flex text-[10px]">K</Kbd>
            </Button>

            <Modal isOpen={isOpen} onClose={close} placement="top" size="lg" hideCloseButton>
                <ModalContent>
                    <ModalBody className="p-0">
                        <div className="flex items-center gap-3 px-4 py-3 border-b-1 dark:border-gray-700">
                            <Search className="w-5 h-5 text-gray-400 shrink-0" aria-hidden="true" />
                            <input
                                ref={inputRef}
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                onKeyDown={handleInputKeyDown}
                                placeholder="Jump to a tool..."
                                aria-label="Search tools"
                                className="w-full bg-transparent outline-none text-gray-900 dark:text-gray-100 placeholder:text-gray-400"
                            />
                            <Kbd className="text-[10px] shrink-0">ESC</Kbd>
                        </div>
                        <ul role="listbox" aria-label="Tool results" className="max-h-80 overflow-y-auto py-2">
                            {filtered.length === 0 && (
                                <li className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
                                    No tools match &quot;{query}&quot;
                                </li>
                            )}
                            {filtered.map((tool, index) => (
                                <li key={tool.path} role="option" aria-selected={index === activeIndex}>
                                    <button
                                        type="button"
                                        onMouseEnter={() => setActiveIndex(index)}
                                        onClick={() => navigateTo(tool.path)}
                                        className={
                                            "w-full flex items-center gap-3 px-4 py-2.5 text-left " +
                                            (index === activeIndex
                                                ? "bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-blue-300"
                                                : "text-gray-700 dark:text-gray-300")
                                        }
                                    >
                                        <ToolIcon value={tool.value} className="w-4 h-4 shrink-0" />
                                        <span className="flex-1">{tool.label}</span>
                                        <span className="text-xs text-gray-400 dark:text-gray-500">{tool.group}</span>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </ModalBody>
                </ModalContent>
            </Modal>
        </>
    );
}
